import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Property } from '@/hooks/useProperties';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface SavedComparison {
  id: string;
  name: string;
  property_ids: string[];
  created_at: string;
  updated_at: string;
}

interface ComparisonContextType {
  comparisonList: Property[];
  addToComparison: (property: Property) => void;
  removeFromComparison: (propertyId: string) => void;
  clearComparison: () => void;
  isInComparison: (propertyId: string) => boolean;
  showComparison: boolean;
  setShowComparison: (show: boolean) => void;
  maxComparisonItems: number;
  // Saved comparisons
  savedComparisons: SavedComparison[];
  saveComparison: (name: string) => Promise<void>;
  loadSavedComparison: (comparison: SavedComparison, allProperties: Property[]) => void;
  deleteSavedComparison: (id: string) => Promise<void>;
  loadingSaved: boolean;
}

const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined);

const MAX_COMPARISON_ITEMS = 4;

export function ComparisonProvider({ children }: { children: React.ReactNode }) {
  const [comparisonList, setComparisonList] = useState<Property[]>([]);
  const [showComparison, setShowComparison] = useState(false);
  const [savedComparisons, setSavedComparisons] = useState<SavedComparison[]>([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  // Load saved comparisons when user is authenticated
  useEffect(() => {
    if (user) {
      loadSavedComparisons();
    } else {
      setSavedComparisons([]);
    }
  }, [user]);

  const loadSavedComparisons = async () => {
    if (!user) return;
    
    setLoadingSaved(true);
    try {
      const { data, error } = await supabase
        .from('saved_comparisons')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSavedComparisons(data || []);
    } catch (error) {
      console.error('Error loading saved comparisons:', error);
    } finally {
      setLoadingSaved(false);
    }
  };

  const addToComparison = useCallback((property: Property) => {
    setComparisonList(prev => {
      if (prev.some(p => p.id === property.id)) {
        return prev;
      }
      if (prev.length >= MAX_COMPARISON_ITEMS) {
        toast({
          title: 'Maximum reached',
          description: `You can compare up to ${MAX_COMPARISON_ITEMS} properties at once`,
          variant: 'destructive',
        });
        return prev;
      }
      return [...prev, property];
    });
  }, [toast]);

  const removeFromComparison = useCallback((propertyId: string) => {
    setComparisonList(prev => prev.filter(p => p.id !== propertyId));
  }, []);

  const clearComparison = useCallback(() => {
    setComparisonList([]);
    setShowComparison(false);
  }, []);

  const isInComparison = useCallback((propertyId: string) => {
    return comparisonList.some(p => p.id === propertyId);
  }, [comparisonList]);

  const saveComparison = async (name: string) => {
    if (!user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to save comparisons',
        variant: 'destructive',
      });
      return;
    }

    if (comparisonList.length < 2) {
      toast({
        title: 'Not enough properties',
        description: 'Add at least 2 properties to save a comparison',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('saved_comparisons')
        .insert({
          user_id: user.id,
          name,
          property_ids: comparisonList.map(p => p.id),
        });

      if (error) throw error;

      toast({
        title: 'Comparison saved',
        description: `"${name}" has been saved to your account`,
      });

      await loadSavedComparisons();
    } catch (error) {
      console.error('Error saving comparison:', error);
      toast({
        title: 'Error',
        description: 'Failed to save comparison',
        variant: 'destructive',
      });
    }
  };

  const loadSavedComparison = (comparison: SavedComparison, allProperties: Property[]) => {
    const properties = comparison.property_ids
      .map(id => allProperties.find(p => p.id === id))
      .filter((p): p is Property => p !== undefined);

    if (properties.length < 2) {
      toast({
        title: 'Some properties unavailable',
        description: 'Some properties in this comparison are no longer available',
        variant: 'destructive',
      });
    }

    setComparisonList(properties);
    if (properties.length >= 2) {
      setShowComparison(true);
    }
  };

  const deleteSavedComparison = async (id: string) => {
    try {
      const { error } = await supabase
        .from('saved_comparisons')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Comparison deleted',
      });

      await loadSavedComparisons();
    } catch (error) {
      console.error('Error deleting comparison:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete comparison',
        variant: 'destructive',
      });
    }
  };

  return (
    <ComparisonContext.Provider
      value={{
        comparisonList,
        addToComparison,
        removeFromComparison,
        clearComparison,
        isInComparison,
        showComparison,
        setShowComparison,
        maxComparisonItems: MAX_COMPARISON_ITEMS,
        savedComparisons,
        saveComparison,
        loadSavedComparison,
        deleteSavedComparison,
        loadingSaved,
      }}
    >
      {children}
    </ComparisonContext.Provider>
  );
}

export function useComparison() {
  const context = useContext(ComparisonContext);
  if (context === undefined) {
    throw new Error('useComparison must be used within a ComparisonProvider');
  }
  return context;
}
